/**
 * STORY-038: Demandes d'intervention
 * File d'attente pour les admins quand l'IA ne sait pas repondre
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Bug,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Code,
  FolderPlus,
  HelpCircle,
  Lightbulb,
  MessageSquare,
  Send,
  ShieldAlert,
  User,
  UserCheck,
  Wrench,
  XCircle,
} from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Spinner } from '@/components/ui/Spinner'
import { Breadcrumb } from '@/components/ui/Breadcrumb'
import { EmptyState } from '@/components/ui/EmptyState'
import { Modal } from '@/components/ui/Modal'
import {
  useInterventionRequests,
  useUpdateIntervention,
  useResolveIntervention,
  useAIConversation,
} from '@/hooks/useAI'
import { useAuth } from '@/contexts/AuthContext'
import {
  type InterventionRequest,
} from '@/types/ai'
import { cn } from '@/lib/utils'

// Priority config
const PRIORITY_CONFIG: Record<
  InterventionRequest['priority'],
  { label: string; variant: 'red' | 'gold' | 'blue' | 'gray'; icon: typeof AlertCircle }
> = {
  urgent: { label: 'Urgent', variant: 'red', icon: AlertCircle },
  high: { label: 'Haute', variant: 'gold', icon: AlertTriangle },
  medium: { label: 'Moyenne', variant: 'blue', icon: Clock },
  low: { label: 'Basse', variant: 'gray', icon: HelpCircle },
}

// Reason/type config with distinct colors and icons
const REASON_CONFIG: Record<
  string,
  { label: string; variant: 'mint' | 'gold' | 'brown' | 'beige' | 'red' | 'blue' | 'gray' | 'default'; icon: typeof AlertCircle }
> = {
  // Client request types
  creation_projet: { label: 'Creation de projet', variant: 'mint', icon: FolderPlus },
  support: { label: 'Support technique', variant: 'blue', icon: Wrench },
  bug_report: { label: 'Rapport de bug', variant: 'red', icon: Bug },
  feature_request: { label: 'Demande de fonctionnalite', variant: 'brown', icon: Lightbulb },
  autre: { label: 'Autre demande', variant: 'gray', icon: HelpCircle },
  // AI intervention reasons
  complex_technical_question: { label: 'Question technique', variant: 'blue', icon: Code },
  pricing_negotiation: { label: 'Negociation tarifaire', variant: 'gold', icon: MessageSquare },
  complaint: { label: 'Reclamation client', variant: 'red', icon: AlertTriangle },
  urgent_request: { label: 'Demande urgente', variant: 'red', icon: AlertCircle },
  off_topic: { label: 'Hors sujet', variant: 'gray', icon: HelpCircle },
  explicit_human_request: { label: 'Demande humain', variant: 'beige', icon: User },
  ai_uncertainty: { label: 'Incertitude IA', variant: 'gold', icon: HelpCircle },
  sensitive_information: { label: 'Info sensible', variant: 'brown', icon: ShieldAlert },
}

// Status config - handle both AI intervention statuses and client request statuses
const STATUS_CONFIG: Record<
  string,
  { label: string; variant: 'gold' | 'blue' | 'mint' | 'gray' | 'red' }
> = {
  // AI intervention statuses
  pending: { label: 'En attente', variant: 'gold' },
  in_progress: { label: 'En cours', variant: 'blue' },
  resolved: { label: 'Resolue', variant: 'mint' },
  dismissed: { label: 'Ignoree', variant: 'gray' },
  // Client request statuses
  draft: { label: 'Brouillon', variant: 'gray' },
  submitted: { label: 'Soumise', variant: 'gold' },
  in_review: { label: 'En révision', variant: 'blue' },
  approved: { label: 'Approuvée', variant: 'mint' },
  rejected: { label: 'Rejetée', variant: 'red' },
  completed: { label: 'Terminée', variant: 'mint' },
}

export default function InterventionsPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [priorityFilter, setPriorityFilter] = useState<InterventionRequest['priority'] | ''>('')
  const [reasonFilter, setReasonFilter] = useState<string>('')
  const [showDrafts, setShowDrafts] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [respondingTo, setRespondingTo] = useState<InterventionRequest | null>(null)
  const [responseText, setResponseText] = useState('')
  const [adminNotes, setAdminNotes] = useState('')

  const { data: interventions, isLoading } = useInterventionRequests({
    status: statusFilter || undefined,
    priority: priorityFilter || undefined,
  })

  const updateIntervention = useUpdateIntervention()
  const resolveIntervention = useResolveIntervention()

  // Get conversation for the responding modal
  const { data: respondingConversation } = useAIConversation(respondingTo?.conversationId)

  const handleTakeOver = (intervention: InterventionRequest) => {
    if (!user?.id) return
    updateIntervention.mutate({
      id: intervention.id,
      updates: { status: 'in_progress', assignedAdminId: user.id },
    })
  }

  const handleDismiss = (interventionId: string) => {
    updateIntervention.mutate({
      id: interventionId,
      updates: { status: 'dismissed' },
    })
  }

  const handleRespond = (intervention: InterventionRequest) => {
    setRespondingTo(intervention)
    setResponseText('')
    setAdminNotes('')
  }

  const handleSubmitResponse = () => {
    if (!respondingTo || !responseText.trim() || !user?.id) return

    resolveIntervention.mutate(
      {
        interventionId: respondingTo.id,
        adminId: user.id,
        response: responseText,
        notes: adminNotes || undefined,
      },
      {
        onSuccess: () => {
          setRespondingTo(null)
          setResponseText('')
          setAdminNotes('')
        },
      }
    )
  }

  const handleViewConversation = (conversationId: string) => {
    navigate(`/ai/questionnaire/${conversationId}`)
  }

  // Group interventions by priority for urgent display
  const urgentInterventions = interventions?.filter((i) => i.priority === 'urgent' && (i.status === 'pending' || i.status === 'submitted')) || []
  const draftCount = interventions?.filter((i) => i.status === 'draft').length || 0
  const pendingCount = interventions?.filter((i) => i.status === 'pending' || i.status === 'submitted').length || 0

  const breadcrumbItems = [{ label: 'Interventions' }]

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-brown-900">
            Demandes d'intervention
          </h1>
          <p className="mt-1 text-brown-600">
            Gerez les demandes quand l'IA necessite une intervention humaine
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="red" size="md">
            {urgentInterventions.length} urgentes
          </Badge>
          <Badge variant="gold" size="md">
            {pendingCount} en attente
          </Badge>
          {draftCount > 0 && (
            <Badge variant="gray" size="md">
              {draftCount} brouillon{draftCount > 1 ? 's' : ''}
            </Badge>
          )}
        </div>
      </div>

      {/* Urgent interventions alert */}
      {urgentInterventions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-lg p-4"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-red-800">
                {urgentInterventions.length} intervention{urgentInterventions.length > 1 ? 's' : ''} urgente{urgentInterventions.length > 1 ? 's' : ''}
              </h3>
              <p className="text-sm text-red-700 mt-1">
                Ces demandes necessitent une attention immediate
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Filters */}
      <Card variant="bordered" padding="sm">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full sm:w-48"
          >
            <option value="">Tous les statuts</option>
            {Object.entries(STATUS_CONFIG)
              .filter(([value]) => value !== 'draft')
              .map(([value, config]) => (
              <option key={value} value={value}>
                {config.label}
              </option>
            ))}
          </Select>
          <Select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as InterventionRequest['priority'] | '')}
            className="w-full sm:w-48"
          >
            <option value="">Toutes les priorites</option>
            {Object.entries(PRIORITY_CONFIG).map(([value, config]) => (
              <option key={value} value={value}>
                {config.label}
              </option>
            ))}
          </Select>
          <Select
            value={reasonFilter}
            onChange={(e) => setReasonFilter(e.target.value)}
            className="w-full sm:w-56"
          >
            <option value="">Tous les types</option>
            {Object.entries(REASON_CONFIG).map(([value, config]) => (
              <option key={value} value={value}>
                {config.label}
              </option>
            ))}
          </Select>
          <button
            type="button"
            onClick={() => setShowDrafts(!showDrafts)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap',
              showDrafts
                ? 'bg-brown-100 text-brown-800'
                : 'bg-beige-50 text-brown-500 hover:bg-beige-100'
            )}
          >
            <div className={cn(
              'w-3 h-3 rounded-sm border-2 transition-colors',
              showDrafts ? 'bg-brown-600 border-brown-600' : 'border-brown-300'
            )} />
            Brouillons {draftCount > 0 && `(${draftCount})`}
          </button>
        </div>
      </Card>

      {/* Interventions list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : interventions?.length === 0 ? (
        <EmptyState
          icon={<CheckCircle2 className="w-12 h-12" />}
          title="Aucune intervention en attente"
          description="Toutes les demandes ont ete traitees"
        />
      ) : (
        <div className="space-y-4">
          {interventions
            ?.filter((i) => showDrafts || i.status !== 'draft')
            .filter((i) => !reasonFilter || i.reason === reasonFilter)
            .map((intervention) => {
            const priorityConfig = PRIORITY_CONFIG[intervention.priority] || PRIORITY_CONFIG.medium
            const statusConfig = STATUS_CONFIG[intervention.status] || { label: intervention.status, variant: 'gray' as const }
            const reasonConfig = REASON_CONFIG[intervention.reason] || { label: intervention.reason, variant: 'gray' as const, icon: HelpCircle }
            const isExpanded = expandedId === intervention.id
            const PriorityIcon = priorityConfig.icon
            const ReasonIcon = reasonConfig.icon

            return (
              <motion.div
                key={intervention.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                layout
              >
                <Card
                  variant="bordered"
                  className={cn(
                    'transition-all cursor-pointer hover:shadow-md border-l-4',
                    intervention.priority === 'urgent' &&
                      (intervention.status === 'pending' || intervention.status === 'submitted')
                      ? 'border-l-red-500 bg-red-50/30'
                      : reasonConfig.variant === 'mint' ? 'border-l-mint-500'
                      : reasonConfig.variant === 'blue' ? 'border-l-blue-500'
                      : reasonConfig.variant === 'red' ? 'border-l-red-400'
                      : reasonConfig.variant === 'gold' ? 'border-l-gold-500'
                      : reasonConfig.variant === 'brown' ? 'border-l-brown-400'
                      : reasonConfig.variant === 'beige' ? 'border-l-beige-400'
                      : 'border-l-gray-300'
                  )}
                  onClick={() => navigate(`/interventions/${intervention.id}`)}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div
                        className={cn(
                          'w-10 h-10 rounded-full flex items-center justify-center shrink-0',
                          `bg-${reasonConfig.variant}-100`
                        )}
                      >
                        <ReasonIcon
                          className={cn('w-5 h-5', `text-${reasonConfig.variant}-600`)}
                        />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant={reasonConfig.variant} size="sm">
                            {reasonConfig.label}
                          </Badge>
                          <Badge variant={priorityConfig.variant} size="sm">
                            <PriorityIcon className="w-3 h-3 mr-1 inline" />
                            {priorityConfig.label}
                          </Badge>
                          <Badge variant={statusConfig.variant} size="sm">
                            {statusConfig.label}
                          </Badge>
                        </div>
                        <p className="font-medium text-brown-900 mt-1">
                          {intervention.lastUserMessage.length > 100
                            ? `${intervention.lastUserMessage.slice(0, 100)}...`
                            : intervention.lastUserMessage}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-brown-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(intervention.createdAt).toLocaleString('fr-FR')}
                          </span>
                          {intervention.assignedAdminId && (
                            <span className="flex items-center gap-1">
                              <UserCheck className="w-3 h-3" />
                              Assigne
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 sm:flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedId(isExpanded ? null : intervention.id)}
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>

                      {(intervention.status === 'pending' || intervention.status === 'submitted' || intervention.status === 'draft') && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleTakeOver(intervention)}
                            disabled={updateIntervention.isPending}
                          >
                            <UserCheck className="w-4 h-4 mr-1" />
                            Prendre en charge
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDismiss(intervention.id)}
                            disabled={updateIntervention.isPending}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </>
                      )}

                      {intervention.status === 'in_progress' && (
                        <Button size="sm" onClick={() => handleRespond(intervention)}>
                          <Send className="w-4 h-4 mr-1" />
                          Repondre
                        </Button>
                      )}

                      {intervention.conversationId && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewConversation(intervention.conversationId)}
                        >
                          <MessageSquare className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Expanded context */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-4 pt-4 border-t border-beige-200">
                          <h4 className="text-sm font-medium text-brown-900 mb-2">
                            {intervention.conversationId ? 'Contexte de la conversation' : 'Description de la demande'}
                          </h4>
                          <div className="bg-beige-50 rounded-lg p-4 text-sm text-brown-700 whitespace-pre-wrap">
                            {intervention.context || 'Aucune description disponible'}
                          </div>

                          {intervention.adminNotes && (
                            <div className="mt-4">
                              <h4 className="text-sm font-medium text-brown-900 mb-2">
                                Notes de l'admin
                              </h4>
                              <p className="text-sm text-brown-600">{intervention.adminNotes}</p>
                            </div>
                          )}

                          {intervention.conversationId && (
                            <div className="mt-4 flex justify-end">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewConversation(intervention.conversationId)}
                              >
                                Voir la conversation complete
                                <ArrowRight className="w-4 h-4 ml-1" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              </motion.div>
            )
          })}
        </div>
      )}

      {/* Response modal */}
      <Modal
        isOpen={!!respondingTo}
        onClose={() => setRespondingTo(null)}
        size="lg"
        title="Repondre a la demande"
      >
        {respondingTo && (
          <div className="space-y-4">
            {/* Last user message */}
            <div>
              <label className="block text-sm font-medium text-brown-700 mb-2">
                Message du client
              </label>
              <div className="bg-beige-100 rounded-lg p-3 text-sm text-brown-700">
                <div className="flex items-center gap-2 mb-1 text-brown-500">
                  <User className="w-3 h-3" />
                  Client
                </div>
                {respondingTo.lastUserMessage}
              </div>
            </div>

            {/* Context */}
            <div>
              <label className="block text-sm font-medium text-brown-700 mb-2">
                Contexte
              </label>
              <div className="bg-beige-50 rounded-lg p-3 text-sm text-brown-600 max-h-40 overflow-y-auto">
                {respondingTo.context}
              </div>
            </div>

            {/* Recent conversation */}
            {respondingConversation && respondingConversation.messages.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-brown-700 mb-2">
                  Messages recents
                </label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {respondingConversation.messages.slice(-5).map((msg) => (
                    <div
                      key={msg.id}
                      className={cn(
                        'p-2 rounded-lg text-xs',
                        msg.role === 'user' ? 'bg-beige-100 ml-4' : 'bg-mint-50 mr-4'
                      )}
                    >
                      <span className="font-medium">
                        {msg.role === 'user' ? 'Client' : 'IA'}:
                      </span>{' '}
                      {msg.content.length > 100
                        ? `${msg.content.slice(0, 100)}...`
                        : msg.content}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Response input */}
            <div>
              <label className="block text-sm font-medium text-brown-700 mb-2">
                Votre reponse au client
              </label>
              <textarea
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                placeholder="Redigez votre reponse..."
                className="w-full h-32 px-3 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
              />
            </div>

            {/* Admin notes */}
            <div>
              <label className="block text-sm font-medium text-brown-700 mb-2">
                Notes internes (optionnel)
              </label>
              <Input
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Notes pour l'equipe..."
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t border-beige-200">
              <Button variant="outline" onClick={() => setRespondingTo(null)}>
                Annuler
              </Button>
              <Button
                onClick={handleSubmitResponse}
                disabled={!responseText.trim() || resolveIntervention.isPending}
              >
                {resolveIntervention.isPending ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Envoi...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer la reponse
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
